
import { Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { AbstractControl, AsyncValidatorFn, FormBuilder, FormGroup, ValidationErrors } from '@angular/forms';
import { first, map, Observable, of, Subscription } from 'rxjs';

import { ButtonTypesEnum } from '../../../Forms/enums/button-types.enum';
import { messageSeverityEnum } from '../../../Misc/enums/message-severity.enum';

import { InputValidationModel } from '../../../Forms/models/input-validation.model';

import { BrandIdentityService } from '../../services/brand-identity.service';
import { BrandIdentity } from '../../models/brand-identity';

@Component({
    selector: 'agpr-brand-information',
    templateUrl: './brand-information.component.html',
    styleUrls: ['./brand-information.component.scss'],
    standalone: false
})
export class BrandInformationComponent implements OnInit, OnDestroy {
    readonly buttonTypes = ButtonTypesEnum;
    readonly messageSeverityEnum = messageSeverityEnum;
    readonly splitOnPattern: RegExp = /(")?,(?=(?:[^\"]*\"[^\"]*\")*[^\"]*$)\1 */g;
    readonly URL_REGEXP = /^(http(s)?:\/\/)[\w.-]+(?:\.[\w\.-]+)+[\w\-\._~:/?#[\]@%!\$&'\(\)\*\+,;=.]+$/;
    readonly websiteChipValidations = [
        new InputValidationModel({
            requirements: this.URL_REGEXP,
            exclusions: null,
            errorKey: 'Common.brandInformation.socialLinks.url.invalid',
        })
    ];

    private subscriptions: Subscription[] = [];
    private cachedBrand: BrandIdentity;

    @Input() id: string = 'brandInformationDialog';
    @Output() selectBrand = new EventEmitter<number>();

    brand: BrandIdentity;
    brandForm: FormGroup;
    canSave: boolean = false;

    @ViewChild('brandFormContainer') brandFormContainer: ElementRef;

    constructor(
        private fb: FormBuilder,
        private brandIdentityService: BrandIdentityService
    ) {
        this.createForm();
    }

    ngOnInit(): void {
        this.subscriptions.push(
            this.brandIdentityService.currentResource$.subscribe(brand => {
                this.brand = brand.clone() ?? new BrandIdentity();
                this.cachedBrand = this.brand.clone();
                this.resetForm();
                requestAnimationFrame(() => {
                    this.brandFormContainer.nativeElement.scrollIntoView({ block: 'start', behavior: 'smooth' });
                });
            })
        );
    }

    // getters for each control
    get name(): AbstractControl {
        return this.brandForm.get('name');
    }
    get logo(): AbstractControl {
        return this.brandForm.get('logo');
    }
    get nameVariations(): AbstractControl {
        return this.brandForm.get('nameVariations');
    }
    get colors(): AbstractControl {
        return this.brandForm.get('colors');
    }
    get competitors(): AbstractControl {
        return this.brandForm.get('competitors');
    }
    get topics(): AbstractControl {
        return this.brandForm.get('topics');
    }
    get websites(): AbstractControl {
        return this.brandForm.get('websites');
    }
    get socialLinks(): AbstractControl {
        return this.brandForm.get('socialLinks');
    }
    get bio(): AbstractControl {
        return this.brandForm.get('bio');
    }
    get tagline(): AbstractControl {
        return this.brandForm.get('tagline');
    }
    get missionStatement(): AbstractControl {
        return this.brandForm.get('missionStatement');
    }
    get products(): AbstractControl {
        return this.brandForm.get('products');
    }
    get brandVoice(): AbstractControl {
        return this.brandForm.get('brandVoice');
    }
    get keyValuePropositions(): AbstractControl {
        return this.brandForm.get('keyValuePropositions');
    }
    get industry(): AbstractControl {
        return this.brandForm.get('industry');
    }
    get customInfo(): AbstractControl {
        return this.brandForm.get('customInfo');
    }

    private createForm() {
        this.brandForm = this.fb.group({
            name: ['', { updateOn: 'blur', asyncValidators: [this.nameValidator()] }],
            logo: [''],
            nameVariations: [[]],
            colors: [[]],
            competitors: [[]],
            topics: [[]],
            websites: [[]],
            socialLinks: [[]],
            bio: ['', { updateOn: 'blur' }],
            tagline: ['', { updateOn: 'blur' }],
            missionStatement: ['', { updateOn: 'blur' }],
            products: ['', { updateOn: 'blur' }],
            brandVoice: ['', { updateOn: 'blur' }],
            keyValuePropositions: ['', { updateOn: 'blur' }],
            industry: ['', { updateOn: 'blur' }],
            customInfo: [''],
        });

        this.subscriptions.push(
            this.brandForm.valueChanges.subscribe(value => {
                const brandName = this.brand.name;
                Object.assign(this.brand, value);
                if (brandName !== value.name) {
                    this.brand.setLogoPlaceholder();
                }
                const canSave = this.updateState();
                if (canSave) {
                    this.save();
                }
            })
        );
    }

    private resetForm() {
        this.brandForm.reset({
            name: this.brand.name,
            logo: this.brand.logo,
            nameVariations: this.brand.nameVariations,
            colors: this.brand.colors,
            competitors: this.brand.competitors,
            topics: this.brand.topics,
            websites: this.brand.websites,
            socialLinks: this.brand.socialLinks,
            bio: this.brand.bio,
            tagline: this.brand.tagline,
            missionStatement: this.brand.missionStatement,
            products: this.brand.products,
            brandVoice: this.brand.brandVoice,
            keyValuePropositions: this.brand.keyValuePropositions,
            industry: this.brand.industry,
            customInfo: this.brand.customInfo,
        }, { emitEvent: false });
    }

    updateState(): boolean {
        return !this.brandForm.pending // brand form is not pending
            && this.brandForm.valid // has no errors in the fields
            && !this.brand.equals(this.cachedBrand); // there are changes
    }

    nameValidator(): AsyncValidatorFn {
        return (control: AbstractControl): Observable<ValidationErrors> => {
            const name = control.value?.trim();

            if (!name) {
                return of({ emptyName: true });
            }

            return this.brandIdentityService.isNameDuplicated$(name, this.brand?.id).pipe(
                map(isDuplicated => (isDuplicated ? { nameExists: true } : null)),
                first()
            );
        };
    }

    onCreateCompetitor(name: string) {
        const newBrand = new BrandIdentity();
        newBrand.name = name;
        newBrand.linkedBrand = this.brand;
        this.brandIdentityService.setCurrentResource(newBrand);
    }

    onSelectCompetitor(brandId: number) {
        this.selectBrand.emit(brandId);
    }

    save() {
        const request = (this.brand?.id || this.brand?.id === 0)
            ? this.brandIdentityService.update(this.brand)
            : this.brandIdentityService.create(this.brand);
        request.subscribe(brand => {
            this.brandIdentityService.setCurrentResource(brand);
        });
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }
}
